<template>
    <div class="container mx-auto px-4 py-8">
        <div class="bg-white rounded-lg shadow-lg p-6">
            <!-- Header -->
            <div class="flex items-center justify-between mb-6">
                <div class="flex items-center gap-3">
                    <font-awesome-icon :icon="['fab', 'whatsapp']" class="text-3xl text-green-500" />
                    <h1 class="text-2xl font-bold text-gray-800">Configuración de WhatsApp</h1>
                </div>
                <button @click="testConnection" :disabled="loadingTest"
                    class="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 active:scale-95 disabled:opacity-50 flex items-center gap-2">
                    <svg v-if="loadingTest" class="w-4 h-4 animate-spin">
                        <use href="@/assets/svg/icon.svg#spinnerForBlue" />
                    </svg>
                    <span>{{ loadingTest ? 'Probando...' : 'Probar Conexión' }}</span>
                </button>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="flex justify-center items-center py-12">
                <svg class="w-8 h-8 animate-spin text-blue-500">
                    <use href="@/assets/svg/icon.svg#spinnerForBlue" />
                </svg>
            </div>

            <!-- Configuration Form -->
            <form v-else @submit.prevent="saveConfiguration" class="space-y-6">
                <!-- Estado General -->
                <div class="border-b pb-4">
                    <h2 class="text-lg font-semibold text-gray-700 mb-4">Estado General</h2>
                    <div class="grid md:grid-cols-2 gap-6">
                        <label class="flex flex-col cursor-pointer">
                            <span class="block mb-2 text-sm font-medium text-gray-800">
                                Sistema habilitado
                            </span>
                            <div class="relative inline-flex items-center h-6 cursor-pointer">
                                <input type="checkbox" class="sr-only peer" v-model="config.habilitado" />
                                <div
                                    class="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-1 after:left-[4px] after:bg-white after:border-transparent after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500">
                                </div>
                                <span class="ml-3 text-sm font-medium text-gray-600">
                                    {{ config.habilitado ? "Activo" : "Inactivo" }}
                                </span>
                            </div>
                        </label>

                        <label class="flex flex-col cursor-pointer">
                            <span class="block mb-2 text-sm font-medium text-gray-800">
                                Envío automático
                            </span>
                            <div class="relative inline-flex items-center h-6 cursor-pointer">
                                <input type="checkbox" class="sr-only peer" v-model="config.envio_automatico" />
                                <div
                                    class="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-1 after:left-[4px] after:bg-white after:border-transparent after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500">
                                </div>
                                <span class="ml-3 text-sm font-medium text-gray-600">
                                    {{ config.envio_automatico ? "SI" : "NO" }}
                                </span>
                            </div>
                        </label>

                        <label class="flex flex-col cursor-pointer">
                            <span class="block mb-2 text-sm font-medium text-gray-800">
                                Requiere confirmación
                            </span>
                            <div class="relative inline-flex items-center h-6 cursor-pointer">
                                <input type="checkbox" class="sr-only peer" v-model="config.requiere_confirmacion" />
                                <div
                                    class="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-1 after:left-[4px] after:bg-white after:border-transparent after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500">
                                </div>
                                <span class="ml-3 text-sm font-medium text-gray-600">
                                    {{ config.requiere_confirmacion ? "SI" : "NO" }}
                                </span>
                            </div>
                        </label>
                    </div>
                </div>

                <!-- Credenciales API (solo mostrar si está configurado) -->
                <div class="border-b pb-4">
                    <h2 class="text-lg font-semibold text-gray-700 mb-4">Credenciales API</h2>
                    <div class="bg-blue-50 border-l-4 border-blue-500 p-4">
                        <div class="flex items-start">
                            <font-awesome-icon :icon="['fas', 'circle-info']" class="text-blue-500 mt-1 mr-3" />
                            <div>
                                <p class="text-sm text-gray-700">
                                    <strong>Estado:</strong>
                                    <span :class="config.api_configured ? 'text-green-600' : 'text-red-600'">
                                        {{ config.api_configured ? 'Configurado en servidor' : 'No configurado' }}
                                    </span>
                                </p>
                                <p class="text-xs text-gray-600 mt-2">
                                    Las credenciales de API (Token y Phone Number ID) se configuran en el archivo .env
                                    del servidor por seguridad.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Límites -->
                <div class="border-b pb-4">
                    <h2 class="text-lg font-semibold text-gray-700 mb-4">Límites de Envío</h2>
                    <div class="grid md:grid-cols-2 gap-6">
                        <label class="block">
                            <span class="block mb-2 text-sm font-medium text-gray-800">
                                Máximo envíos por día
                            </span>
                            <input type="number"
                                class="py-2 px-3 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500"
                                v-model.number="config.max_envios_por_dia" min="0" placeholder="1000" />
                        </label>
                    </div>
                </div>

                <!-- Horarios -->
                <div class="border-b pb-4">
                    <h2 class="text-lg font-semibold text-gray-700 mb-4">Horario de Envío</h2>
                    <div class="grid md:grid-cols-2 gap-6">
                        <label class="block">
                            <span class="block mb-2 text-sm font-medium text-gray-800">
                                Hora inicio
                            </span>
                            <input type="time"
                                class="py-2 px-3 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500"
                                v-model="config.horario_inicio" />
                        </label>

                        <label class="block">
                            <span class="block mb-2 text-sm font-medium text-gray-800">
                                Hora fin
                            </span>
                            <input type="time"
                                class="py-2 px-3 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500"
                                v-model="config.horario_fin" />
                        </label>
                    </div>

                    <div class="mt-4">
                        <span class="block mb-2 text-sm font-medium text-gray-800">
                            Días hábiles para envío
                        </span>
                        <div class="grid grid-cols-7 gap-2">
                            <label v-for="dia in diasSemana" :key="dia.value"
                                class="flex flex-col items-center cursor-pointer">
                                <input type="checkbox" :value="dia.value" v-model="diasHabilesArray"
                                    class="sr-only peer" />
                                <div
                                    class="w-10 h-10 flex items-center justify-center rounded-full border-2 peer-checked:bg-blue-500 peer-checked:text-white peer-checked:border-blue-500 text-gray-600 font-semibold text-sm">
                                    {{ dia.label }}
                                </div>
                            </label>
                        </div>
                    </div>
                </div>

                <!-- Mensaje Predeterminado -->
                <div class="border-b pb-4">
                    <h2 class="text-lg font-semibold text-gray-700 mb-4">Mensaje Predeterminado</h2>
                    <label class="block">
                        <textarea rows="6"
                            class="py-2 px-3 text-sm bg-gray-50 outline-none rounded-lg text-gray-600 w-full border-2 focus:border-blue-500"
                            v-model="config.mensaje_predeterminado" placeholder="Mensaje con variables..."></textarea>
                        <p class="text-xs text-gray-500 mt-2">
                            Variables disponibles: {cliente}, {comprobante}, {total}, {moneda}, {fecha}
                        </p>
                    </label>
                </div>

                <!-- Botones de Acción -->
                <div class="flex gap-4 pt-4">
                    <button type="submit" :disabled="saving"
                        class="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 active:scale-95 disabled:opacity-50 flex items-center gap-2 font-semibold">
                        <svg v-if="saving" class="w-4 h-4 animate-spin">
                            <use href="@/assets/svg/icon.svg#spinnerForBlue" />
                        </svg>
                        <span>{{ saving ? 'Guardando...' : 'Guardar Configuración' }}</span>
                    </button>

                    <button type="button" @click="resetToDefaults" :disabled="saving"
                        class="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 active:scale-95 disabled:opacity-50 font-semibold">
                        Restaurar por Defecto
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { errorActions, confirmationOfRecord } from '@/alerts';
import { observer } from '@/observer';
import axios from '@/api';

export default {
    name: 'WhatsAppConfiguration',
    setup() {
        const loading = ref(false);
        const saving = ref(false);
        const loadingTest = ref(false);

        const config = ref({
            habilitado: false,
            envio_automatico: false,
            mensaje_predeterminado: '',
            max_envios_por_dia: 1000,
            horario_inicio: '08:00',
            horario_fin: '20:00',
            dias_habiles: '',
            requiere_confirmacion: false,
            api_configured: false
        });

        const diasSemana = [
            { label: 'L', value: '1' },
            { label: 'M', value: '2' },
            { label: 'M', value: '3' },
            { label: 'J', value: '4' },
            { label: 'V', value: '5' },
            { label: 'S', value: '6' },
            { label: 'D', value: '0' }
        ];

        const diasHabilesArray = computed({
            get() {
                return config.value.dias_habiles ? config.value.dias_habiles.split(',') : [];
            },
            set(value) {
                config.value.dias_habiles = value.join(',');
            }
        });

        const loadConfiguration = async () => {
            loading.value = true;
            try {
                const response = await axios.get('/whatsapp/config');
                if (response.data.success) {
                    Object.assign(config.value, response.data.data);
                }
            } catch (error) {
                console.error('Error cargando configuración:', error);
                errorActions('Error al cargar la configuración');
                observer.observe(
                    document.querySelector('.swal2-container'),
                    { box: 'content-box' },
                    10
                );
            } finally {
                loading.value = false;
            }
        };

        const saveConfiguration = async () => {
            saving.value = true;
            try {
                const response = await axios.put('/whatsapp/config', config.value);

                if (response.data.success) {
                    confirmationOfRecord('Configuración guardada exitosamente');
                } else {
                    errorActions(response.data.message || 'Error al guardar configuración');
                }

                observer.observe(
                    document.querySelector('.swal2-container'),
                    { box: 'content-box' },
                    10
                );
            } catch (error) {
                console.error('Error guardando configuración:', error);
                const errorMessage = error.response?.data?.message || 'Error al guardar la configuración';
                errorActions(errorMessage);
                observer.observe(
                    document.querySelector('.swal2-container'),
                    { box: 'content-box' },
                    10
                );
            } finally {
                saving.value = false;
            }
        };

        const testConnection = async () => {
            loadingTest.value = true;
            try {
                const response = await axios.post('/whatsapp/config/test');

                if (response.data.success) {
                    confirmationOfRecord('Conexión exitosa con la API de WhatsApp');
                } else {
                    errorActions(response.data.message || 'Error en la conexión');
                }

                observer.observe(
                    document.querySelector('.swal2-container'),
                    { box: 'content-box' },
                    10
                );
            } catch (error) {
                console.error('Error probando conexión:', error);
                errorActions('No se pudo conectar con la API de WhatsApp');
                observer.observe(
                    document.querySelector('.swal2-container'),
                    { box: 'content-box' },
                    10
                );
            } finally {
                loadingTest.value = false;
            }
        };

        const resetToDefaults = async () => {
            if (!confirm('¿Estás seguro de restaurar la configuración por defecto?')) {
                return;
            }

            saving.value = true;
            try {
                const response = await axios.post('/whatsapp/config/reset');

                if (response.data.success) {
                    Object.assign(config.value, response.data.data);
                    confirmationOfRecord('Configuración restaurada a valores por defecto');
                }

                observer.observe(
                    document.querySelector('.swal2-container'),
                    { box: 'content-box' },
                    10
                );
            } catch (error) {
                console.error('Error restaurando configuración:', error);
                errorActions('Error al restaurar configuración');
                observer.observe(
                    document.querySelector('.swal2-container'),
                    { box: 'content-box' },
                    10
                );
            } finally {
                saving.value = false;
            }
        };

        onMounted(() => {
            loadConfiguration();
        });

        return {
            loading,
            saving,
            loadingTest,
            config,
            diasSemana,
            diasHabilesArray,
            saveConfiguration,
            testConnection,
            resetToDefaults
        };
    }
};
</script>

<style scoped>
/* Estilos adicionales si son necesarios */
</style>
